import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Switch,
  TableSortLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  PersonAdd,
  AdminPanelSettings,
  SupervisorAccount,
  Person,
  Login,
  Restore
} from '@mui/icons-material';
import axios from 'axios';
import { formatDateDisplay } from '../../utils/dateUtils';
import { useAuth } from '../../hooks/useAuth';

const UserManager = () => {
  const { user } = useAuth();
  const canManageEnterpriseAdmins = user?.role === 'enterprise_admin';
  const canImpersonateUser = (rowUser) => {
    if (!user || rowUser.id === user.id) return false;
    if (user.role === 'enterprise_admin') return true;
    if (user.role === 'admin') return rowUser.role !== 'enterprise_admin';
    if (user.role === 'manager') return rowUser.role === 'employee';
    return false;
  };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToImpersonate, setUserToImpersonate] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'employee',
    password: '',
    kiosk_pin: '',
    data_collection: false,
    scoped_can_use_existing_customers: true,
    scoped_see_others_documents: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/employees', { params: { activeOnly: false } });
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'employee',
        password: '',
        kiosk_pin: user.kiosk_pin || '',
        data_collection: user.data_collection === true,
        scoped_can_use_existing_customers: user.scoped_can_use_existing_customers !== false,
        scoped_see_others_documents: user.scoped_see_others_documents === true
      });
    } else {
      setEditingUser(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: 'employee',
        password: '',
        kiosk_pin: '',
        data_collection: false,
        scoped_can_use_existing_customers: true,
        scoped_see_others_documents: false
      });
    }
    setOpen(true);
    setError('');
    setSuccess('');
    setShowPassword(false);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: 'employee',
      password: '',
      kiosk_pin: '',
      data_collection: false,
      scoped_can_use_existing_customers: true,
      scoped_see_others_documents: false
    });
    setError('');
    setSuccess('');
    setShowPassword(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        // Update existing user — preserve HR fields not shown in this form
        const updateData = {
          ...editingUser,
          ...formData,
          is_active: editingUser.is_active
        };
        delete updateData.id;
        delete updateData.created_at;
        delete updateData.updated_at;
        delete updateData.password_hash;
        if (!formData.password) {
          delete updateData.password;
        }
        await axios.put(`/api/employees/${editingUser.id}`, updateData);
        setSuccess('User updated successfully');
      } else {
        // Create new user
        await axios.post('/api/employees', formData);
        setSuccess('User created successfully');
      }
      handleCloseDialog();
      loadUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      setError(error.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDelete = (userId) => {
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await axios.delete(`/api/employees/${userToDelete}`);
      setSuccess('User deleted successfully');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError(error.response?.data?.message || 'Failed to delete user');
      setDeleteDialogOpen(false);
    }
  };

  const handleReactivate = async (userId) => {
    try {
      await axios.post(`/api/employees/${userId}/reactivate`);
      setSuccess('User reactivated successfully');
      loadUsers();
    } catch (error) {
      console.error('Failed to reactivate user:', error);
      setError(error.response?.data?.message || 'Failed to reactivate user');
    }
  };

  const handleImpersonate = (userId) => {
    setUserToImpersonate(userId);
    setImpersonateDialogOpen(true);
  };

  const confirmImpersonate = async () => {
    if (!userToImpersonate) return;

    try {
      const response = await axios.post('/api/auth/impersonate', { targetUserId: userToImpersonate });
      const { token } = response.data.data;
      
      // Update the token and reload the page
      localStorage.setItem('token', token);
      window.location.reload();
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      setError(error.response?.data?.message || 'Failed to impersonate user');
      setImpersonateDialogOpen(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'enterprise_admin':
        return <AdminPanelSettings color="warning" />;
      case 'admin':
        return <AdminPanelSettings color="error" />;
      case 'manager':
        return <SupervisorAccount color="primary" />;
      case 'employee':
        return <Person color="action" />;
      default:
        return <Person color="action" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'enterprise_admin':
        return 'warning';
      case 'admin':
        return 'error';
      case 'manager':
        return 'primary';
      case 'employee':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparator = (property) => {
    return (a, b) => {
      const direction = order === 'asc' ? 1 : -1;

      const getValue = () => {
        switch (property) {
          case 'name': {
            const aLast = (a.last_name || '').toLowerCase();
            const bLast = (b.last_name || '').toLowerCase();
            if (aLast !== bLast) return aLast < bLast ? -1 * direction : 1 * direction;
            const aFirst = (a.first_name || '').toLowerCase();
            const bFirst = (b.first_name || '').toLowerCase();
            if (aFirst === bFirst) return 0;
            return aFirst < bFirst ? -1 * direction : 1 * direction;
          }
          case 'email': {
            const aVal = (a.email || '').toLowerCase();
            const bVal = (b.email || '').toLowerCase();
            if (aVal === bVal) return 0;
            return aVal < bVal ? -1 * direction : 1 * direction;
          }
          case 'phone': {
            const aVal = (a.phone || '').toLowerCase();
            const bVal = (b.phone || '').toLowerCase();
            if (aVal === bVal) return 0;
            return aVal < bVal ? -1 * direction : 1 * direction;
          }
          case 'hireDate': {
            const aVal = a.date_of_hire || '';
            const bVal = b.date_of_hire || '';
            if (!aVal && !bVal) return 0;
            if (!aVal) return 1; // nulls last
            if (!bVal) return -1;
            if (aVal === bVal) return 0;
            return aVal < bVal ? -1 * direction : 1 * direction;
          }
          case 'role': {
            const aVal = (a.role || '').toLowerCase();
            const bVal = (b.role || '').toLowerCase();
            if (aVal === bVal) return 0;
            return aVal < bVal ? -1 * direction : 1 * direction;
          }
          case 'status': {
            const aVal = !!a.is_active;
            const bVal = !!b.is_active;
            if (aVal === bVal) return 0;
            return aVal === bVal ? 0 : aVal ? -1 * direction : 1 * direction;
          }
          default:
            return 0;
        }
      };

      return getValue();
    };
  };

  const sortedUsers = React.useMemo(() => {
    const usersCopy = [...users];
    const comparator = getComparator(orderBy);
    return usersCopy.sort(comparator);
  }, [users, orderBy, order]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'email' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'phone' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'phone'}
                  direction={orderBy === 'phone' ? order : 'asc'}
                  onClick={() => handleRequestSort('phone')}
                >
                  Phone
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'hireDate' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'hireDate'}
                  direction={orderBy === 'hireDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('hireDate')}
                >
                  Hire Date
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'role' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'role'}
                  direction={orderBy === 'role' ? order : 'asc'}
                  onClick={() => handleRequestSort('role')}
                >
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getRoleIcon(user.role)}
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {user.first_name} {user.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        #{user.employee_number}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.phone || 'N/A'}</TableCell>
                <TableCell>{user.date_of_hire ? formatDateDisplay(user.date_of_hire) : '—'}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    color={getRoleColor(user.role)}
                    size="small"
                    icon={getRoleIcon(user.role)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.is_active ? 'Active' : 'Inactive'}
                    color={user.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{user.location || '—'}</TableCell>
                <TableCell>
                  <Tooltip title="Edit User">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(user)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  {canImpersonateUser(user) ? (
                    <Tooltip title="Impersonate User">
                      <IconButton
                        size="small"
                        onClick={() => handleImpersonate(user.id)}
                        color="primary"
                      >
                        <Login />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Only Enterprise Admins can impersonate Enterprise Admin accounts">
                      <span>
                        <IconButton size="small" color="primary" disabled>
                          <Login />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {!user.is_active && (
                    <Tooltip title="Reactivate User">
                      <IconButton
                        size="small"
                        onClick={() => handleReactivate(user.id)}
                        color="success"
                      >
                        <Restore />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete User">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(user.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit User Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Kiosk PIN"
                  name="kiosk_pin"
                  value={formData.kiosk_pin}
                  onChange={(e) => setFormData({ ...formData, kiosk_pin: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  placeholder="For clock in/out at kiosk (e.g. 123)"
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.data_collection === true}
                      onChange={(e) => setFormData({ ...formData, data_collection: e.target.checked })}
                    />
                  }
                  label="Data Collection — show on Clocked In page"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    label="Role"
                  >
                    <MenuItem value="employee">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person sx={{ mr: 1 }} />
                        Employee
                      </Box>
                    </MenuItem>
                    <MenuItem value="sales_rep">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person sx={{ mr: 1 }} />
                        Outside Sales Rep
                      </Box>
                    </MenuItem>
                    <MenuItem value="estimator">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person sx={{ mr: 1 }} />
                        Estimator (scoped quotes)
                      </Box>
                    </MenuItem>
                    <MenuItem value="manager">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SupervisorAccount sx={{ mr: 1 }} />
                        Manager
                      </Box>
                    </MenuItem>
                    <MenuItem value="admin">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AdminPanelSettings sx={{ mr: 1 }} />
                        Admin
                      </Box>
                    </MenuItem>
                    {canManageEnterpriseAdmins ? (
                      <MenuItem value="enterprise_admin">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AdminPanelSettings sx={{ mr: 1 }} />
                          Enterprise Admin
                        </Box>
                      </MenuItem>
                    ) : (
                      formData.role === 'enterprise_admin' ? (
                        <MenuItem value="enterprise_admin" disabled>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AdminPanelSettings sx={{ mr: 1 }} />
                            Enterprise Admin (requires Enterprise Admin)
                          </Box>
                        </MenuItem>
                      ) : null
                    )}
                  </Select>
                </FormControl>
              </Grid>
              {formData.role === 'estimator' && (
                <>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.scoped_can_use_existing_customers !== false}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              scoped_can_use_existing_customers: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Can use existing customers (off = only customers they create/source)"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.scoped_see_others_documents === true}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              scoped_see_others_documents: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Can see others' quotes / orders / packing slips"
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={editingUser ? "New Password (leave blank to keep current)" : "Password"}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  inputProps={{ autoComplete: 'new-password' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show password"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        aria-labelledby="delete-user-dialog-title"
        aria-describedby="delete-user-dialog-description"
      >
        <DialogTitle id="delete-user-dialog-title">
          Delete User?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-user-dialog-description">
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setUserToDelete(null);
          }} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Impersonate User Confirmation Dialog */}
      <Dialog
        open={impersonateDialogOpen}
        onClose={() => {
          setImpersonateDialogOpen(false);
          setUserToImpersonate(null);
        }}
        aria-labelledby="impersonate-user-dialog-title"
        aria-describedby="impersonate-user-dialog-description"
      >
        <DialogTitle id="impersonate-user-dialog-title">
          Impersonate User?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="impersonate-user-dialog-description">
            Are you sure you want to impersonate this user? You will be logged in as them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setImpersonateDialogOpen(false);
            setUserToImpersonate(null);
          }} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmImpersonate} color="primary" variant="contained" autoFocus>
            Impersonate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManager;
